import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Search, Settings } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { ScrollArea, ScrollBar } from 'components/ui/scroll-area';
import { Skeleton } from 'components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useRefreshMCPServer } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IToolDetails, IToolMcpAction } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { ActionDetailsModal } from './action-details-modal';
import { ConfigParamsModal } from './config-params-modal';
import { getMcpActions, getMcpConfig } from './tool-detail-utils';

const PAGE_SIZE = 10;

interface McpServerConfigurationProps {
  readonly tool: IToolDetails;
}

export function McpServerConfiguration({ tool }: McpServerConfigurationProps) {
  const { t } = useTranslation();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canEdit = useIsOwner(tool);
  const { mutateAsync: refreshMCPServer, isPending: isRefreshing } = useRefreshMCPServer();
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<IToolMcpAction | null>(null);
  const [isActionDetailsOpen, setIsActionDetailsOpen] = useState(false);

  const mcpConfig = getMcpConfig(tool);
  const actions = getMcpActions(tool);

  const filteredData = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return actions;

    return actions.filter(
      (item) =>
        (item.Name || '').toLowerCase().includes(normalized) ||
        (item.Description || '').toLowerCase().includes(normalized)
    );
  }, [actions, search]);

  const totalCount = filteredData.length;
  const currentPageNumber = Math.min(pageNumber, Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1));
  const paginatedData = useMemo(() => {
    const startIndex = currentPageNumber * PAGE_SIZE;
    return filteredData.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPageNumber, filteredData]);

  const isMCPConfigEmpty = useMemo(() => !mcpConfig?.ServerURL || mcpConfig.ServerURL.trim() === '', [mcpConfig]);
  const configParamsLabel = t('CONFIG_PARAMS') || 'Config Params';
  const configurationLabel = t('CONFIGURATION') || 'Configuration';
  const noDescriptionLabel = t('NO_DESCRIPTION') || 'No description';

  const handleRefreshMCP = async () => {
    try {
      toast({
        variant: 'info',
        title: t('REFRESHING') || 'Refreshing...',
        description: t('UPDATING_MCP_SERVER_DATA') || 'Updating MCP Server data',
      });

      const result = await refreshMCPServer({ toolId: tool.id, projectKey, isBlocksTool: tool.is_open_tool });
      if (!result.is_success) {
        throw new Error(
          result.message || result.detail || t('FAILED_TO_REFRESH_MCP_SERVER') || 'Failed to refresh MCP server'
        );
      }

      toast({
        variant: 'success',
        title: t('REFRESH_SUCCESSFUL') || 'Refresh successful',
        description:
          result.detail || t('MCP_SERVER_REFRESHED_SUCCESSFULLY') || 'MCP server refreshed successfully.',
      });
      await queryClient.invalidateQueries({ queryKey: ['tools', 'detail', tool.id, projectKey] });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('REFRESH_FAILED') || 'Refresh failed',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_REFRESH_MCP_SERVER') || 'Failed to refresh MCP server.',
      });
    }
  };

  if (!mcpConfig && !tool.MCPConfig) {
    return (
      <Card className="flex flex-col gap-2 rounded shadow-none">
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[100px] w-full" />
      </Card>
    );
  }

  if (isMCPConfigEmpty) {
    return (
      <Card className="rounded shadow-none">
        <CardContent className="flex min-h-[400px] flex-col items-center justify-center p-12">
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <Settings className="h-10 w-10 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                {t('NO_CONFIGURATION_FOUND') || 'No Configuration Found'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('CONFIGURE_MCP_SERVER_TO_GET_STARTED') ||
                  'Configure your MCP server parameters to get started'}
              </p>
            </div>
            <Button
              onClick={() => setIsConfigModalOpen(true)}
              size="lg"
              disabled={!canEdit}
            >
              {configParamsLabel}
            </Button>
          </div>
        </CardContent>
        <ConfigParamsModal
          open={isConfigModalOpen}
          onOpenChange={setIsConfigModalOpen}
          name={configurationLabel}
          toolId={tool.id}
          mcpConfig={mcpConfig}
          isBlocksTool={tool.is_open_tool}
          projectKey={projectKey}
        />
      </Card>
    );
  }

  return (
    <>
      <Card className="min-w-0 overflow-hidden rounded shadow-none">
        <CardContent className="min-w-0 p-0">
          <div className="flex flex-col gap-4 px-4 pt-0 pb-0 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:pt-4 sm:pb-6">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPageNumber(0);
                }}
                placeholder={t('SEARCH') || 'Search'}
                className="h-10 pl-9 sm:h-9"
              />
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
              <Button
                variant="outline"
                className="h-10 w-full sm:h-9 sm:w-auto"
                onClick={handleRefreshMCP}
                disabled={!canEdit || isRefreshing}
              >
                {isRefreshing ? t('UPDATING') || 'Updating...' : t('UPDATE_API') || 'Update API'}
              </Button>
              <Button
                className="h-10 w-full sm:h-9 sm:w-auto"
                onClick={() => setIsConfigModalOpen(true)}
                disabled={!canEdit}
              >
                {configParamsLabel}
              </Button>
            </div>
          </div>

          {paginatedData.length === 0 ? (
            <p className="px-4 pb-6 text-center text-sm text-muted-foreground sm:px-6">
              {t('NO_ACTIONS_FOUND') || 'No actions found.'}
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-3 px-4 pb-4 sm:px-6 lg:hidden">
                {paginatedData.map((row) => (
                  <div key={row.actionId} className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 flex-1 break-all font-medium text-foreground">
                        {row.Name || row.actionId}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 shrink-0 gap-2"
                        onClick={() => {
                          setSelectedAction(row);
                          setIsActionDetailsOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                        {t('VIEW') || 'View'}
                      </Button>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {row.Description || noDescriptionLabel}
                    </p>
                  </div>
                ))}
              </div>

              <ScrollArea className="hidden w-full lg:block">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="min-w-[140px]">{t('NAME') || 'Name'}</TableHead>
                      <TableHead className="min-w-[200px]">{t('DESCRIPTION') || 'Description'}</TableHead>
                      <TableHead className="w-[100px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((row) => (
                      <TableRow key={row.actionId}>
                        <TableCell className="max-w-[200px] font-medium">
                          <span className="line-clamp-2 break-all">{row.Name || row.actionId}</span>
                        </TableCell>
                        <TableCell className="max-w-md text-muted-foreground">
                          <span className="line-clamp-3">{row.Description || noDescriptionLabel}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedAction(row);
                              setIsActionDetailsOpen(true);
                            }}
                            className="h-8 gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            {t('VIEW') || 'View'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </>
          )}

          {totalCount > PAGE_SIZE ? (
            <div className="flex flex-col gap-3 border-t border-border/40 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-muted-foreground">
                {t('TOOLS_SHOWING_ENTRIES', {
                  from: currentPageNumber * PAGE_SIZE + 1,
                  to: Math.min((currentPageNumber + 1) * PAGE_SIZE, totalCount),
                  total: totalCount,
                  defaultValue: 'Showing {{from}}-{{to}} of {{total}} entries',
                }) ||
                  `Showing ${currentPageNumber * PAGE_SIZE + 1}-${Math.min((currentPageNumber + 1) * PAGE_SIZE, totalCount)} of ${totalCount} entries`}
              </p>
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  onClick={() => setPageNumber(Math.max(0, currentPageNumber - 1))}
                  disabled={currentPageNumber === 0}
                >
                  {t('PREVIOUS') || 'Previous'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  onClick={() => setPageNumber(currentPageNumber + 1)}
                  disabled={(currentPageNumber + 1) * PAGE_SIZE >= totalCount}
                >
                  {t('NEXT') || 'Next'}
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <ConfigParamsModal
        open={isConfigModalOpen}
        onOpenChange={setIsConfigModalOpen}
        name={configurationLabel}
        toolId={tool.id}
        mcpConfig={mcpConfig}
        isBlocksTool={tool.is_open_tool}
        projectKey={projectKey}
      />
      <ActionDetailsModal open={isActionDetailsOpen} onOpenChange={setIsActionDetailsOpen} action={selectedAction} />
    </>
  );
}
